import React from 'react';
import classnames from 'classnames/bind';

import { type DataWidgetFiltersInput } from 'dashboards/types/filter';

import { isNotNullOrUndefined } from 'services/nullable';

import { Button, ButtonProps } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { Icon } from 'components/Icon/IconDeprecated';

import {
  LogicalOperator,
  LogicalOperatorSelect,
} from './LogicalOperatorSelect';
import { DataWidgetFilter } from './DataWidgetFilter';
import { DataWidgetFilterGroup } from './DataWidgetFilterGroup';
import { DataWidgetFiltersInputContext } from './context';
import styles from './DataWidgetFiltersInput.module.scss';

const c = classnames.bind(styles);

export type DataWidgetFiltersInputContextProps =
  | {
      filters: DataWidgetFiltersInput[];
      columns: Array<{ name: string; datatype: string }>;
      addFilter: (logical: LogicalOperator, parent?: string) => void;
      updateFilter: (
        id: string,
        values: Partial<
          Pick<DataWidgetFiltersInput, 'column' | 'operator' | 'value'>
        >
      ) => void;
      updateLogicalOperator: (logical: LogicalOperator, id: string) => void;
      removeFilter: (id: string) => void;
    }
  | undefined;

type DataWidgetFiltersInputProps = {
  // Available columns used as suggestions
  // Changes based on which metric or dataPool is selected
  columns: Array<{ name: string; datatype: string }>;
  value?: DataWidgetFiltersInput[];
  onChange?: (newValue: DataWidgetFiltersInput[]) => void;
} & Pick<ButtonProps, 'disabled' | 'disabledTooltip'>;

export function DataWidgetFiltersInput({
  columns,
  value = [],
  onChange,
  disabled,
  disabledTooltip,
}: DataWidgetFiltersInputProps) {
  const [rootFilter] = value;

  const [logicalOperator, setLogicalOperator] = React.useState<LogicalOperator>(
    rootFilter?.or ? 'OR' : 'AND'
  );

  function insertFilter(
    logical: LogicalOperator,
    parent: string,
    newFilter: DataWidgetFiltersInput,
    filters: DataWidgetFiltersInput[]
  ): DataWidgetFiltersInput[] {
    return filters.map((filter) => {
      if (filter.id === parent) {
        if (logical === 'AND') {
          return {
            ...filter,
            and: filter?.and ? [...filter.and, newFilter] : [newFilter],
          };
        }

        if (logical === 'OR') {
          return {
            ...filter,
            or: filter?.or ? [...filter.or, newFilter] : [newFilter],
          };
        }
      }

      if (filter?.and) {
        return {
          ...filter,
          and: insertFilter(logical, parent, newFilter, filter.and),
        };
      }

      if (filter?.or) {
        return {
          ...filter,
          or: insertFilter(logical, parent, newFilter, filter.or),
        };
      }

      return filter;
    });
  }

  /**
   * Adds a new filter.
   * @param logical - The logical operator used to connect the new filter with the parent filter.
   * @param parent - Id of the parent filter.
   */
  function addFilter(logical: LogicalOperator, parent?: string) {
    const newFilter: DataWidgetFiltersInput = {
      id: crypto.randomUUID(),
      column: '',
      operator: 'EQUALS',
      value: '',
    };

    if (!parent) {
      onChange?.([newFilter]);
      return;
    }

    onChange?.(insertFilter(logical, parent, newFilter, value));
  }

  function handleAddFilter() {
    addFilter(logicalOperator, rootFilter?.id);
  }

  function handleAddFilterGroup() {
    const newFilterGroup: DataWidgetFiltersInput = {
      id: crypto.randomUUID(),
      column: '',
      operator: 'EQUALS',
      value: '',
      and: [],
    };

    if (!value || value.length === 0) {
      onChange?.([newFilterGroup]);
      return;
    }

    onChange?.(
      insertFilter(logicalOperator, rootFilter.id, newFilterGroup, value)
    );
  }

  function updateFilter(
    id: string,
    values: Partial<
      Pick<DataWidgetFiltersInput, 'column' | 'operator' | 'value'>
    >
  ) {
    const updateProperty = (
      filter: DataWidgetFiltersInput
    ): DataWidgetFiltersInput => {
      if (filter.id === id) {
        return { ...filter, ...values };
      }

      if (filter?.and || filter?.or) {
        return {
          ...filter,
          and: filter.and?.map(updateProperty),
          or: filter.or?.map(updateProperty),
        };
      }

      return filter;
    };

    onChange?.(value.map(updateProperty));
  }

  function switchLogicalOperator(
    logical: LogicalOperator,
    id: string,
    filters: DataWidgetFiltersInput[]
  ): DataWidgetFiltersInput[] {
    return filters.map((filter) => {
      if (filter.id === id) {
        if (logical === 'AND' && filter?.or) {
          const { and, or, ...rest } = filter;

          return {
            ...rest,
            and: or,
          };
        }

        if (logical === 'OR' && filter?.and) {
          const { and, or, ...rest } = filter;

          return {
            ...rest,
            or: and,
          };
        }
      }

      if (filter?.and) {
        return {
          ...filter,
          and: switchLogicalOperator(logical, id, filter.and),
        };
      }

      if (filter?.or) {
        return {
          ...filter,
          or: switchLogicalOperator(logical, id, filter.or),
        };
      }

      return filter;
    });
  }

  /**
   * Changes how filters are logically connected.
   * @param logical - Logical operator to switch to.
   * @param id - Id of the root filter.
   */
  function updateLogicalOperator(logical: LogicalOperator, id: string) {
    onChange?.(switchLogicalOperator(logical, id, value));
  }

  /**
   * Removes a filter by Id.
   * If a root filter is removed the first filter inside `and` or `or` will be promoted to become the new root filter.
   */
  function deleteFilter(id: string, filters: DataWidgetFiltersInput[]) {
    return filters
      .map((filter) => {
        if (filter.id === id && !filter?.and && !filter?.or) {
          return null;
        }

        if (filter.id === id && filter?.and) {
          const promotedFilter = filter.and.shift();

          return promotedFilter
            ? { ...promotedFilter, and: [...filter.and] }
            : null;
        }

        if (filter.id === id && filter?.or) {
          const promotedFilter = filter.or.shift();

          return promotedFilter
            ? { ...promotedFilter, or: [...filter.or] }
            : null;
        }

        if (filter?.and) {
          filter.and = deleteFilter(id, filter.and);
        }

        if (filter?.or) {
          filter.or = deleteFilter(id, filter.or);
        }

        return filter;
      })
      .filter(isNotNullOrUndefined);
  }

  function removeFilter(id: string) {
    onChange?.(deleteFilter(id, value));
  }

  return (
    <DataWidgetFiltersInputContext.Provider
      value={{
        filters: value,
        columns,
        addFilter,
        updateFilter,
        updateLogicalOperator,
        removeFilter,
      }}
    >
      <div className={c('input')}>
        {value.map((filter) => (
          <div className={c('group')} key={filter.id}>
            <div className={c('group-item')}>
              <LogicalOperatorSelect
                id={`logical-operator-switch-${filter.id}`}
                value={logicalOperator}
                onChange={(logical) => {
                  if (logical === 'AND' || logical === 'OR') {
                    updateLogicalOperator(logical, rootFilter.id);
                    setLogicalOperator(logical);
                  }
                }}
              />
            </div>

            <DataWidgetFilter {...filter} />

            {filter?.and?.map((andFilter) => {
              if (andFilter?.and || andFilter?.or) {
                return (
                  <DataWidgetFilterGroup key={andFilter.id} {...andFilter} />
                );
              }

              return <DataWidgetFilter key={andFilter.id} {...andFilter} />;
            })}

            {filter?.or?.map((orFilter) => {
              if (orFilter?.and || orFilter?.or) {
                return (
                  <DataWidgetFilterGroup key={orFilter.id} {...orFilter} />
                );
              }

              return <DataWidgetFilter key={orFilter.id} {...orFilter} />;
            })}
          </div>
        ))}

        <ButtonGroup>
          <Button
            variant="secondary"
            size="xsmall"
            onClick={handleAddFilter}
            disabledTooltip={disabledTooltip}
            disabled={disabled}
          >
            <Icon name="plus" size="xsmall" />
            <span>Add filter</span>
          </Button>
          <Button
            variant="secondary"
            size="xsmall"
            onClick={handleAddFilterGroup}
            disabled={!value?.length || disabled}
          >
            <Icon name="plus" size="xsmall" />
            <span>Add filter group</span>
          </Button>
        </ButtonGroup>
      </div>
    </DataWidgetFiltersInputContext.Provider>
  );
}
